// Carrier-portal import helpers, shared by the Carriers screen: the Portal
// accounts tab, one account's history and the tenant-wide All updates drawer.
//
// The Integrations → Allianz modal keeps its own copies on purpose — it is the
// older door onto the same accounts and is deliberately not touched here. The
// one exception is the portal filter below, which it must share: that screen is
// Allianz-only and has to ignore every other carrier's login.

export const ALLIANZ_PORTAL = 'allianz_mx';

/**
 * An account the Allianz-only screens may show and run. A missing portal is a
 * row created before accounts carried one, and those were all Allianz.
 */
export function isAllianzAccount(account) {
  return Boolean(account) && (!account.portal || account.portal === ALLIANZ_PORTAL);
}

/**
 * The file a payments read's receipts download as, named after the carrier whose read it is
 * (job type `handle.payment.sync` → handle, `zurich.payment.scrape` → zurich), on the local day.
 * Built here because the browser cannot see the server's Content-Disposition across origins.
 */
export function receiptsFileName(jobType, now = new Date(), what = 'receipts') {
  const pad = (n) => String(n).padStart(2, '0');
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const carrier = String(jobType || '').split('.')[0] || 'axa';
  return `${carrier}-${what}-${day}.csv`;
}

/** A run of an Allianz job type (allianz.policy.scrape, allianz.payment.scrape, …). */
export function isAllianzRun(run) {
  return Boolean(run && run.jobType && run.jobType.startsWith('allianz.'));
}

/**
 * What the "Read" dialog offers, each door being the job kinds it starts.
 * POLICIES_SNAPSHOT, not POLICIES: a read started from Carriers holds its
 * result for review instead of writing it.
 */
export const READ_DOORS = [
  { key: 'payments', kinds: ['PAYMENTS'] },
  { key: 'policies', kinds: ['POLICIES_SNAPSHOT'] },
  { key: 'both', kinds: ['PAYMENTS', 'POLICIES_SNAPSHOT'] },
];

/**
 * The doors a portal can actually serve, from the backend's `supportedKinds`.
 * A portal that reads policies but not payments gets no payments door and no
 * "both"; one that reads payments but not policies gets only the payments
 * door. A portal the list does not describe keeps every door, as before this
 * existed.
 */
export function doorsFor(portal) {
  const kinds = portal && Array.isArray(portal.supportedKinds) ? portal.supportedKinds : null;
  if (!kinds) return READ_DOORS;
  const payments = kinds.includes('PAYMENTS');
  const policies = kinds.includes('POLICIES') || kinds.includes('POLICIES_SNAPSHOT');
  return READ_DOORS.filter((door) => (
    (door.key === 'payments' && payments)
    || (door.key === 'policies' && policies)
    || (door.key === 'both' && payments && policies)
  ));
}

/**
 * The one read the Read dialog confirms: everything the login's portal can read, as one
 * queue. "both" where it reads policies and payments, else the single door it has (Handle:
 * payments; Skandia: policies). The dialog used to offer the doors as three cards, and the
 * choice was one more thing to decide for every read that nearly always came out "both".
 */
export function readDoorFor(portal) {
  const doors = doorsFor(portal);
  return doors.find((door) => door.key === 'both') || doors[0] || null;
}

/**
 * Can the portal behind this run's login read payments? Asked before fetching
 * the payment review for a run: the backend answers a run-less question with
 * the tenant's latest payment run, which for an AXA carrier would put another
 * carrier's figures on its card. An unknown portal is assumed able to.
 */
export function runPortalReadsPayments(run, accounts, portals) {
  if (!run) return false;
  // Accounts not loaded yet: not an answer. Runs and accounts load in
  // parallel, and guessing "yes" here fetched another carrier's figures for a
  // run whose portal was simply not known yet. The page asks again once they arrive.
  if (!accounts || !accounts.length) return false;
  const account = (accounts || []).find((a) => a.scrapeAccountId === run.scrapeAccountId);
  const portal = account && (portals || []).find((p) => p.code === account.portal);
  if (!portal || !Array.isArray(portal.supportedKinds)) return true;
  return portal.supportedKinds.includes('PAYMENTS');
}

// The scrape itself takes minutes (a login, a 2FA email, then a policy-by-policy
// crawl), and the import runs after it. Poll slowly and give up rather than
// hammer the API forever if something upstream wedges.
/** A run the daily sync queued rather than a person. */
export function isDailyRun(run) {
  return Boolean(run) && run.trigger === 'DAILY';
}

export const POLL_INTERVAL_MS = 7000;
export const MAX_POLLS = 180; // ~21 minutes

/**
 * True while a run is still moving — scraping, or importing afterwards.
 *
 * The job going DONE is not the end: the import is a second phase and it is the
 * one that actually creates profiles and policies. A terminal status wins over
 * importStatus, though: a job that streamed parts and then died leaves
 * import_status frozen on IMPORTING. CANCEL_REQUESTED is not an ending either —
 * the worker never polls for cancellation, so the scrape runs to completion.
 */
export function isJobActive(job) {
  if (!job) return false;
  if (job.status === 'FAILED' || job.status === 'CANCELLED') return false;
  const running = job.status === 'QUEUED'
    || job.status === 'RUNNING'
    || job.status === 'CANCEL_REQUESTED';
  const importing = job.importStatus === 'PENDING' || job.importStatus === 'IMPORTING';
  return running || importing;
}

/**
 * Job types the backend runs itself, with no scraper worker (Handle's API read).
 * A run takes seconds, so the backend refuses Stop and Proceed for it: start a
 * new one instead. Mirrors ScrapePortal.runsInBackend.
 */
const BACKEND_JOB_TYPES = new Set(['handle.payment.sync']);

export function runsInBackend(job) {
  return Boolean(job) && BACKEND_JOB_TYPES.has(job.jobType);
}

/**
 * A run can be picked up where it left off if it died, or if a person stopped
 * it — Stop ends the attempt, it does not abandon the book, and the checkpoint
 * it built is what makes Proceed cheap. The exceptions fail the same way
 * again: AUTH_FAILED (the password was rejected), TWOFA_SETUP_REQUIRED (the
 * carrier's authenticator app is not set up yet) and TWOFA_REQUIRED (a nightly
 * read met a code page; a new read, with someone to type the code, is the fix).
 *
 * Mirrors RESUMABLE and NON_RESUMABLE in InsuranceScrapeJobResumeAction.
 */
export function canResume(job) {
  if (!job) return false;
  if (runsInBackend(job)) return false;
  if (job.status !== 'FAILED' && job.status !== 'CANCELLED') return false;
  return !['AUTH_FAILED', 'TWOFA_SETUP_REQUIRED', 'TWOFA_REQUIRED'].includes(job.errorCode);
}

/**
 * True while a stop has been asked for but the worker has not confirmed it.
 *
 * It is a real window, not a flicker: the worker only notices between clients,
 * one client can take five minutes, and a re-login inside it can wait ten for a
 * sign-in code. So the UI promises "stopping", never "stopped".
 */
export function isStopping(job) {
  return Boolean(job) && job.status === 'CANCEL_REQUESTED';
}

/**
 * How a portal asks for its second factor at sign-in, from the backend's `twoFaKind`:
 *
 *  NONE          — no second factor
 *  EMAIL         — a code emailed at each sign-in (Allianz): forwarded by a mailbox, or typed
 *  AUTHENTICATOR — a 6-digit code from an authenticator app (AXA), only ever typed, and only
 *                  while a read is on the code screen: it changes every 30 seconds
 *
 * A backend from before `twoFaKind` only says `needsTwoFa`, and every portal that needed a code
 * then was an emailed one. An unknown portal (not in the list) is assumed to email one, as the
 * screens always have: offering the field costs nothing, withholding it strands a run.
 */
export function portalTwoFaKind(portal) {
  if (!portal) return 'EMAIL';
  if (portal.twoFaKind) return portal.twoFaKind;
  return portal.needsTwoFa ? 'EMAIL' : 'NONE';
}

/** POLICIES or PAYMENTS, from the worker job type the run carries. */
export function jobKind(job) {
  return job && String(job.jobType || '').includes('.payment.') ? 'PAYMENTS' : 'POLICIES';
}

/** A fresh id for a read that starts more than one job, so they can be shown as one. */
export function newReadId() {
  const c = typeof crypto !== 'undefined' ? crypto : null;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `read-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Two minutes: how far apart the two jobs of one "Read both" can land in the queue. */
export const READ_PAIR_WINDOW_MS = 2 * 60 * 1000;

/**
 * The runs as reads: one entry per read, holding one job or the two a "Read
 * both" started. Two jobs are one read when they carry the same read id, or --
 * for reads from before the id existed -- when they are one payments and one
 * policies job of the same account, started the same way (both by a person,
 * or both by the daily sync, which reads both kinds together too) within two
 * minutes of each other. The payments read an accept queues (FOLLOW_UP) is
 * never half of a read. A job pairs at most once; the list's order is kept,
 * each read sitting where its newest job sat.
 *
 * `{ key, runs, payments, policies, lead }`: `lead` is the job the read is judged
 * by -- a running one first, else the policies one, else the only one.
 */
export function groupReads(runs) {
  const list = (runs || []).filter(Boolean);
  const paired = {};
  const entries = [];
  const timeOf = (run) => {
    const t = Date.parse(run.createdAt || run.queuedAt || run.startedAt || '');
    return Number.isNaN(t) ? null : t;
  };
  const triggerOf = (run) => String(run.trigger || 'MANUAL').toUpperCase();
  const pairable = (run) => triggerOf(run) !== 'FOLLOW_UP';
  const sameAccount = (a, b) => (a.scrapeAccountId || a.accountRef) === (b.scrapeAccountId || b.accountRef);
  const partnerOf = (run, index) => {
    for (let j = 0; j < list.length; j += 1) {
      const other = list[j];
      if (j === index || paired[other.scrapeJobId]) continue; // eslint-disable-line no-continue
      if (!sameAccount(run, other) || jobKind(other) === jobKind(run)) continue; // eslint-disable-line no-continue
      if (run.readId && other.readId) {
        if (run.readId === other.readId) return other;
      } else if (!run.readId && !other.readId && pairable(run) && pairable(other)
        && triggerOf(run) === triggerOf(other)) {
        const a = timeOf(run);
        const b = timeOf(other);
        if (a !== null && b !== null && Math.abs(a - b) <= READ_PAIR_WINDOW_MS) return other;
      }
    }
    return null;
  };
  list.forEach((run, index) => {
    if (paired[run.scrapeJobId]) return;
    const other = partnerOf(run, index);
    const members = other ? [run, other] : [run];
    members.forEach((m) => { paired[m.scrapeJobId] = true; });
    const payments = members.find((m) => jobKind(m) === 'PAYMENTS') || null;
    const policies = members.find((m) => jobKind(m) !== 'PAYMENTS') || null;
    const lead = members.find(isJobActive) || policies || payments;
    entries.push({
      key: run.readId || run.scrapeJobId,
      // Payments first: the order the read runs them in, and the order to read them in.
      runs: [payments, policies].filter(Boolean),
      payments,
      policies,
      lead,
    });
  });
  return entries;
}

/**
 * What happened to a run. One place for the errorCode → story mapping so the
 * account's own history and All updates never disagree.
 *
 *  stopping         — a person pressed Stop; the worker has not confirmed yet
 *  running          — still scraping or importing
 *  completed        — DONE with something imported or updated
 *  nothingChanged   — DONE, nothing to import
 *  authFailed       — the portal rejected the password
 *  twoFaTimeout     — the sign-in code never arrived
 *  twoFaRequired    — an unattended read found the session expired and the portal asked for a
 *                     code nobody was there to type (AXA's authenticator)
 *  twoFaSetupRequired — the login never set up the portal's authenticator app
 *  rateLimited      — the portal locked the account after too many codes
 *  extractionFailed — logged in, but pages could not be read
 *  dropped          — connection lost / unknown; resumable
 *  importFailed     — scrape fine, our import threw
 *  cancelled        — stopped by a person
 */
export function runVariant(job) {
  if (!job) return null;
  // Before the isJobActive check: CANCEL_REQUESTED is deliberately non-terminal,
  // so it would otherwise read as an ordinary run still in progress.
  if (isStopping(job)) return 'stopping';
  if (isJobActive(job)) return 'running';
  if (job.status === 'CANCELLED') return 'cancelled';
  if (job.status === 'FAILED') {
    switch (job.errorCode) {
      case 'AUTH_FAILED': return 'authFailed';
      case 'TWOFA_TIMEOUT': return 'twoFaTimeout';
      case 'TWOFA_REQUIRED': return 'twoFaRequired';
      case 'TWOFA_SETUP_REQUIRED': return 'twoFaSetupRequired';
      case 'TWOFA_RATE_LIMITED': return 'rateLimited';
      case 'EXTRACTION_FAILED': return 'extractionFailed';
      default: return 'dropped';
    }
  }
  if (job.importStatus === 'FAILED') return 'importFailed';
  // A payments read says what it did in `paymentCounts`, not in the policy
  // counts. Without them -- a run from before they existed -- nothing is known
  // either way, and "nothing changed" would be a claim, so it reads as done.
  if (jobKind(job) === 'PAYMENTS') {
    const c = job.paymentCounts;
    if (!c) return 'completed';
    // Policies held for a decision are an outcome too: a read that only held
    // some is waiting on someone, not "nothing changed".
    const applied = ['policiesChanged', 'instalmentsPaid', 'instalmentsCanceled', 'instalmentsAdded', 'instalmentsRemoved', 'needsReview']
      .some((key) => Number(c[key]) > 0);
    return !applied && Number(c.policiesRead) > 0 ? 'nothingChanged' : 'completed';
  }
  if (!job.importedCount) return 'nothingChanged';
  return 'completed';
}

/**
 * What a payments read did, as pieces for "42 policies read · 17 payment
 * schedules updated · 31 payments marked paid · 3 not found in Renova".
 *
 * Each piece is `{ key, count, label }`, `label` an i18n key for the words
 * after the figure, so the figure can be bold and a translator can still move
 * the words. Policies read always shows; schedules updated whenever the carrier
 * reports it at all (an Allianz read does not); the rest only when non-zero.
 *
 * Null for a run with no `paymentCounts` -- not a payments read, or one from
 * before the counts were kept -- and the caller falls back to what it said before.
 */
export function paymentsLine(run) {
  const c = run && run.paymentCounts;
  if (!c) return null;
  const pieces = [
    ['read', 'policiesRead', 'carriers.import.paymentsLine.read'],
    ['changed', 'policiesChanged', 'carriers.import.paymentsLine.changed'],
    ['paid', 'instalmentsPaid', 'carriers.import.paymentsLine.paid'],
    ['canceled', 'instalmentsCanceled', 'carriers.import.paymentsLine.canceled'],
    ['added', 'instalmentsAdded', 'carriers.import.paymentsLine.added'],
    ['removed', 'instalmentsRemoved', 'carriers.import.paymentsLine.removed'],
    ['notInRenova', 'notInRenova', 'carriers.import.summary.NotInRenova'],
    ['stoppedBilling', 'stoppedBilling', 'carriers.import.paymentsLine.stoppedBilling'],
    // A trusted account's read: paid or cancelled marks the portal's receipts replaced.
    ['trustedOverrides', 'trustedOverrides', 'carriers.import.paymentsLine.trustedOverrides'],
    ['failed', 'failed', 'carriers.import.paymentsLine.failed'],
    // Every carrier but Allianz (AXA, Skandia, GNP, Zurich) holds whole policies for a yes
    // or no, not receipts to review. A run with no job type predates them.
    ['needsReview', 'needsReview', run.jobType && !isAllianzRun(run)
      ? 'carriers.import.paymentsLine.needsDecision'
      : 'carriers.import.paymentsLine.needsReview'],
  ];
  return pieces
    .map(([key, field, label]) => ({ key, count: c[field], label }))
    .filter(({ key, count }) => {
      if (count === null || count === undefined) return false;
      if (key === 'read' || key === 'changed') return true;
      return Number(count) > 0;
    })
    .map((piece) => ({ ...piece, count: Number(piece.count) || 0 }));
}

/** The tone a variant is painted in. */
export function variantTone(variant) {
  switch (variant) {
    case 'running': return 'info';
    case 'stopping': return 'muted';
    case 'completed':
    case 'nothingChanged': return 'success';
    case 'cancelled': return 'muted';
    default: return 'warning';
  }
}

/**
 * A read that staged its policies for review instead of writing them.
 *
 * The run's origin decides, and it is carried as the job type — a read started
 * from Carriers is `*.policy.snapshot`, a read started from Integrations is
 * `*.policy.scrape`. Read off the type rather than a flag so the two can never
 * be confused for one path with a boolean.
 */
export function isSnapshotRun(job) {
  return Boolean(job) && String(job.jobType || '').includes('.snapshot');
}

/**
 * The newest run of a portal account, from a tenant-wide run list. The backend
 * returns runs newest first, so the first match wins.
 */
export function latestJobFor(runs, scrapeAccountId) {
  if (!scrapeAccountId) return null;
  return (runs || []).find((r) => r.scrapeAccountId === scrapeAccountId) || null;
}

/**
 * The sign-in failure [job] started before [account]'s password was last changed.
 *
 * By when the run started, as the backend's ScrapeJobRepo.signInFailed judges it: a run that
 * signed in with the old password and failed a minute after the new one was saved says nothing
 * about the new one. A login's `lastRun` carries no startedAt, so it falls back to createdAt.
 */
function failedBeforePasswordChange(account, job) {
  const changed = Date.parse(account && account.passwordUpdatedAt);
  const started = Date.parse(job && (job.startedAt || job.createdAt));
  return !Number.isNaN(changed) && !Number.isNaN(started) && started <= changed;
}

/**
 * The state of a portal account, from its newest run.
 *
 *  running | needsCode | needsLogin | needsSetup | locked | failed | connected | never | disabled
 */
export function accountState(account, job) {
  if (!account) return 'never';
  if (!account.active) return 'disabled';
  if (!job) return 'never';
  if (isStopping(job)) return 'stopping';
  if (isJobActive(job)) return job.waitingTwoFaAt ? 'needsCode' : 'running';
  switch (runVariant(job)) {
    // A failure from before the password was changed says nothing about the new one.
    case 'authFailed': return failedBeforePasswordChange(account, job) ? 'passwordChanged' : 'needsLogin';
    case 'twoFaTimeout':
    case 'twoFaRequired': return 'needsCode';
    case 'twoFaSetupRequired': return 'needsSetup';
    case 'rateLimited': return 'locked';
    case 'completed':
    case 'nothingChanged': return 'connected';
    case 'cancelled': return 'connected';
    default: return 'failed';
  }
}

/**
 * Profiles an hour, measured. The book crawls at 200-300 depending on how many
 * policies each client holds and how slow the portal is being; the midpoint is
 * honest enough for an estimate and never promises the optimistic end.
 *
 * Profiles, not policies: the crawl knows how many clients a book has as soon as
 * it has the list, and cannot know how many policies they hold until it has
 * opened every one. Only the first has a denominator, so only the first can be
 * turned into a bar.
 */
export const PROFILES_PER_HOUR = 250;

/**
 * Where a run has got to, in profiles.
 *
 * Null until the worker's first streamed part lands — which is after sign-in and
 * any second factor, so it can be minutes into a run. That is a real state and
 * the caller has to render it as one: before it, nothing is known, and a bar
 * drawn at 0% would claim otherwise.
 */
export function runProgress(job) {
  if (!job) return null;
  const total = Number(job.clientsTotal);
  const done = Number(job.clientsDone);
  if (!Number.isFinite(total) || total <= 0) return null;
  const safeDone = Number.isFinite(done) ? Math.min(Math.max(done, 0), total) : 0;
  return { done: safeDone, total, percent: Math.round((safeDone / total) * 100) };
}

/**
 * Does this run count policies rather than profiles? Every carrier but Allianz
 * does (AXA, Skandia, GNP, Zurich, …). A run with no job type predates the others and was
 * Allianz, so it keeps counting profiles.
 */
function countsPolicies(job) {
  return Boolean(job && job.jobType) && !isAllianzRun(job);
}

/**
 * The words after the progress figure, as an i18n key.
 *
 * The Allianz crawl counts profiles: it knows the client list before it knows
 * the policies. Every other carrier's read (AXA, Skandia, GNP, Zurich) counts units of
 * work — each policy plus any document it downloads, planned before the first
 * one — which mean nothing as a number, so it says how much of the book is read.
 */
export function progressTotalKey(job) {
  return countsPolicies(job)
    ? 'carriers.import.running.OfTheBookRead'
    : 'carriers.import.running.OfProfiles';
}

/**
 * How much longer, in words, or null when saying would be guessing.
 *
 * Under 2% there is no rate worth extrapolating from — a first profile that took
 * a slow minute would forecast days. Minutes round to the nearest five, because a
 * number like "37 min left" claims a precision this does not have.
 */
export function etaText(job, t) {
  const p = runProgress(job);
  if (!p || p.percent < 2 || p.done >= p.total) return null;
  // The rate below is Allianz profiles an hour; a policy count has none yet.
  if (countsPolicies(job)) return null;
  const hours = (p.total - p.done) / PROFILES_PER_HOUR;
  if (hours < 1) {
    const minutes = Math.max(5, Math.round((hours * 60) / 5) * 5);
    return t('carriers.import.running.EtaMinutes', { minutes });
  }
  if (hours < 2) return t('carriers.import.running.EtaHourAndAHalf');
  return t('carriers.import.running.EtaHours', { hours: Math.round(hours) });
}

/**
 * The estimate the Read dialog shows for a whole-book read.
 *
 * Only ever computed from a real profile count — the size of the book as the
 * last finished run measured it. With no such run the caller shows a range
 * instead; an hour figure derived from a guess is worse than admitting the guess.
 */
export function policiesEstimate(job) {
  const total = Number(job && job.clientsTotal);
  if (!Number.isFinite(total) || total <= 0) return null;
  return { profiles: total, hours: Math.max(1, Math.ceil(total / PROFILES_PER_HOUR)) };
}

/**
 * The newest run of an account that got far enough to measure the book, whatever
 * happened to it afterwards. A run that died at profile 900 of 2,545 still
 * learned how big the book is.
 */
export function lastMeasuredJob(runs, scrapeAccountId) {
  return (runs || []).find(
    (r) => r.scrapeAccountId === scrapeAccountId && Number(r.clientsTotal) > 0,
  ) || null;
}

export function formatWhen(iso, locale) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString(locale || undefined, {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return d.toLocaleString();
  }
}

export function formatCount(n) {
  return Number(n || 0).toLocaleString('en-US');
}

/** The progress figure itself: "37%" for a policy-counting read, "1,204" Allianz profiles otherwise. */
export function progressFigure(job) {
  const p = runProgress(job);
  if (!p) return '';
  return countsPolicies(job) ? `${p.percent}%` : formatCount(p.done);
}

export function apiError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.message)
    || (err && err.message)
    || fallback
    || '';
}

/**
 * The name a download should be saved under: the server's, from its
 * Content-Disposition, or the fallback when the header is missing or not
 * exposed to the browser.
 */
export function fileNameFrom(contentDisposition, fallback) {
  const header = String(contentDisposition || '');
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch (e) {
      // A malformed encoding falls through to the plain name.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : fallback;
}

/** Hand a fetched file to the browser to save. */
export function saveFile(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * An account's newest run: whichever of the list's own `lastRun` and the polled
 * run list is fresher, so a read started a moment ago shows before the next
 * accounts fetch.
 */
export function freshestRunFor(account, runs) {
  if (!account) return null;
  const polled = latestJobFor(runs, account.scrapeAccountId);
  const listed = account.lastRun ? { ...account.lastRun, scrapeAccountId: account.scrapeAccountId } : null;
  if (!polled) return listed;
  if (!listed) return polled;
  return Date.parse(polled.updatedAt) >= Date.parse(listed.updatedAt) ? polled : listed;
}

/**
 * The portal rejects [account]'s password as it stands: its newest run failed to sign in, and the
 * password has not been changed since. What an "Update password" button beside a run is offered on,
 * so one on an old failure does not ask for a password that has already been replaced.
 */
export function needsNewPassword(account, runs) {
  return Boolean(account) && accountState(account, freshestRunFor(account, runs)) === 'needsLogin';
}

/** How bad each account state is, for picking the one a carrier row shows. */
const STATE_RANK = {
  needsLogin: 0,
  needsSetup: 0,
  locked: 1,
  needsCode: 2,
  failed: 3,
  running: 4,
  stopping: 5,
  connected: 6,
  passwordChanged: 6,
  never: 7,
  disabled: 8,
};

const STATE_TONE = {
  needsLogin: 'bad',
  needsSetup: 'bad',
  locked: 'bad',
  failed: 'bad',
  needsCode: 'warn',
  running: 'info',
  stopping: 'info',
  connected: 'good',
  passwordChanged: 'good',
  never: 'good',
  disabled: 'muted',
};

/**
 * The chip on a carrier's row: its worst account's state, and the policies all its
 * logins brought in. `{ key: 'notConnected' }` for a carrier with no login.
 *
 * `key` is a carriers.import.state.* key, `tone` good | warn | bad | info | muted.
 */
export function carrierState(accounts, runs) {
  const list = (accounts || []).filter(Boolean);
  if (!list.length) {
    return {
      key: 'notConnected', tone: 'muted', policyCount: 0, run: null
    };
  }
  const states = list.map((account) => {
    const run = freshestRunFor(account, runs);
    return { account, run, key: accountState(account, run) };
  });
  states.sort((a, b) => STATE_RANK[a.key] - STATE_RANK[b.key]);
  // A login read once and connected since reads as connected, even before its first run here.
  const worst = states[0];
  const key = worst.key === 'never' ? 'connected' : worst.key;
  const newest = states.map((s) => s.run).filter(Boolean)
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))[0] || null;
  return {
    key,
    tone: STATE_TONE[key] || 'muted',
    policyCount: list.reduce((sum, a) => sum + (Number(a.policyCount) || 0), 0),
    run: newest,
  };
}

/** A run that ended before it was done: stopped, cut off, or unable to read its pages. */
export function stoppedPartWay(run) {
  return ['cancelled', 'dropped', 'extractionFailed'].includes(runVariant(run));
}

/**
 * "today, 09:14", "yesterday, 18:02" or "8 Sep 2026": when a run ended, as the
 * carriers table says it. Today and yesterday by the viewer's own calendar.
 */
export function runWhen(iso, locale, t, now = new Date()) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(locale || undefined, { hour: '2-digit', minute: '2-digit' });
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days === 0) return t('carriers.import.cell.Today', { time });
  if (days === 1) return t('carriers.import.cell.Yesterday', { time });
  return d.toLocaleDateString(locale || undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** ISO weekday (1 = Monday) as the viewer's short name for it. */
export function weekdayName(isoDay, locale, style = 'short') {
  // 2024-01-01 was a Monday.
  const d = new Date(2024, 0, isoDay);
  try {
    return d.toLocaleDateString(locale || undefined, { weekday: style });
  } catch (e) {
    return String(isoDay);
  }
}

/**
 * The words of a schedule: "Daily · 06:30", "Weekly · Mon, Thu · 06:30",
 * "Monthly · day 15 · 06:30". Null when there is none or it is switched off.
 */
export function scheduleSummary(schedule, t, locale) {
  if (!schedule || !schedule.enabled) return null;
  const parts = [t(`carriers.import.schedule.frequency.${schedule.frequency}`)];
  if (schedule.frequency === 'WEEKLY') {
    parts.push((schedule.weekdays || []).map((d) => weekdayName(d, locale)).join(', '));
  }
  if (schedule.frequency === 'MONTHLY' && schedule.monthDay) {
    parts.push(t('carriers.import.schedule.DayN', { day: schedule.monthDay }));
  }
  parts.push(schedule.time);
  return parts.join(' · ');
}

/** Minutes after midnight as "HH:MM". */
export function minutesToTime(minutes) {
  const m = Math.max(0, Math.min(1439, Number(minutes) || 0));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/**
 * The read that tests a password with the least effect: an Allianz payments
 * preview changes nothing; elsewhere a payments read, or the staged policy read,
 * whose changes wait for review. Null when the portal has neither.
 */
export function signInTestRead(account, portals) {
  const portal = (portals || []).find((p) => p.code === (account && account.portal));
  const kinds = (portal && portal.supportedKinds) || [];
  if (isAllianzAccount(account)) return { kind: 'PAYMENTS', dryRun: true };
  if (kinds.includes('PAYMENTS')) return { kind: 'PAYMENTS' };
  if (kinds.includes('POLICIES_SNAPSHOT')) return { kind: 'POLICIES_SNAPSHOT' };
  return null;
}

/**
 * "today", "3 days ago", or the date past a month: how long ago an instant was,
 * in whole days of the viewer's calendar. Empty for a missing value.
 */
export function relativeDay(iso, locale, now = new Date()) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86400000);
  if (days >= 0 && days < 31 && typeof Intl !== 'undefined' && Intl.RelativeTimeFormat) {
    return new Intl.RelativeTimeFormat(locale || undefined, { numeric: 'auto' }).format(-days, 'day');
  }
  return d.toLocaleDateString(locale || undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * "Main · agent01": a portal account by its name and the login it signs in
 * with, or just the login when it has no name. An API-key account has no login
 * worth showing, so it is its name alone.
 */
export function accountDisplayName(account, portals) {
  if (!account) return '';
  const portal = (portals || []).find((p) => p.code === account.portal);
  const login = portal && portal.credentialKind === 'API_KEY' ? null : account.username;
  if (!account.label) return login || '';
  return login && login !== account.label ? `${account.label} · ${login}` : account.label;
}
